import { ArrowUpTray, Plus } from '@medusajs/icons';
import {
  Button,
  Checkbox,
  Drawer,
  Input,
  Label,
  Select,
  Skeleton,
  Switch,
  Table,
  Text,
  toast,
} from '@pcrm/fe/ui-kit';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { ActionMenu, type ActionMenuGroup } from '../../components/action-menu';
import { Field } from '../../components/field';
import { SectionContainer, SectionEmpty, SectionHeader } from '../../components/section';
import { ApiError } from '../../lib/api';
import { formatDate } from '../../lib/format';
import { nomenclatorQueryOptions } from '../settings/settings.api';
import { SendToSigningDrawer } from '../signing/send-to-signing-drawer';
import { subscriptionsQueryOptions } from '../subscriptions/subscriptions.api';
import { CancelDocumentDrawer } from './cancel-document-drawer';
import { useDeleteDocumentPrompt } from './delete-document-prompt';
import { useDocumentActions } from './document-actions';
import { type DocumentOrigin, fromCustomer } from './document-origin';
import {
  ArchivedDocumentsFilter,
  archivedRowProps,
  DocumentNumberCell,
  DocumentSourceMark,
  DocumentStateCell,
} from './document-presentation';
import {
  DOCUMENTS_QUERY_KEY,
  type DocumentFieldValue,
  type DocumentListItem,
  documentsQueryOptions,
  generateDocument,
  generationTemplatesQueryOptions,
  type TemplateFormField,
  templateFormQueryOptions,
} from './documents.api';
import { UploadDocumentDrawer } from './upload-document-form';

/**
 * Registrul de documente al clientului.
 *
 * **Numărul se tipărește ca atare** — `71`, nu `EFX-ANX-000071`: un singur registru per
 * firmă, din care contractele și anexele iau numere în același șir.
 *
 * Ecranul **nu cunoaște tipuri anume**. Alegi un tip din nomenclator și, opțional,
 * abonamentele pentru care se emite: niciunul înseamnă un document al clientului, trei
 * înseamnă trei documente cu trei numere. Un tip nou — proces verbal, adeverință — apare
 * aici fără cod nou.
 *
 * Actele **primite** — urcate aici sau atribuite din căsuța de email — stau în aceeași listă,
 * cu număr din același registru, marcate „Primit" (fondatorul, 2026-09-15: „toate documentele
 * firmei iau număr de registru"). O listă separată ar fi pus operatorul să se uite în două
 * locuri pentru „ce acte are clientul ăsta".
 *
 * **Fără ștergere.** Un document greșit se anulează, cu motiv, iar numărul rămâne consumat.
 * Vezi docs/wiki/domains/document-manager.md
 */
export function CustomerDocuments({
  customerId,
  archived,
}: {
  /** `null` cât timp fișa nu e salvată: un document are nevoie de un client care există. */
  customerId: string | null;
  /** Clientul e arhivat: nu se mai emit acte pe el. Vezi `assertNotArchived` pe server. */
  archived: boolean;
}) {
  const [page, setPage] = useState(1);
  /**
   * Arhivarea **actelor**, cerută explicit — implicit sunt ascunse (#141).
   *
   * ⚠️ **Altceva decât `archived: 'all'` de mai jos.** Acela spune „arată actele chiar dacă
   * **clientul** e arhivat", și e fixat pentru că fișa lui e deschisă anume; ăsta spune ce se
   * face cu actele arhivate ele însele, și e al operatorului. Un singur parametru pentru
   * amândouă ar fi însemnat că tabul unui client arhivat scoate din arhivă și actele.
   */
  const [documentArchived, setDocumentArchived] = useState<'all' | 'only' | undefined>(undefined);
  const query = useQuery({
    // `archived: 'all'` explicit: dacă tocmai clientul ăsta e arhivat, tabul lui trebuie să
    // arate actele, nu o listă goală. Implicitul serverului ascunde arhivații, și e bun —
    // pentru registrul global, nu pentru fișa unui client deschis anume.
    ...documentsQueryOptions({
      customerId: customerId ?? '',
      archived: 'all',
      documentArchived,
      page,
    }),
    enabled: customerId !== null,
  });

  const [generating, setGenerating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [cancelling, setCancelling] = useState<DocumentListItem | null>(null);
  const [sending, setSending] = useState<DocumentListItem | null>(null);
  // Acțiunile vin din același loc ca în registrul „Documente emise": două ecrane cu reguli
  // diferite pentru același act ar fi două adevăruri despre ce se poate face cu el.
  // Ștergerea ciornei își aduce `Prompt`-ul cu ea (#142); ecranul îl randează la sfârșit.
  const deletePrompt = useDeleteDocumentPrompt();
  // Documentul deschis de aici se întoarce aici (#222), nu în registru.
  const origin = customerId === null ? undefined : fromCustomer(customerId);
  const actionsOf = useDocumentActions({
    onCancel: setCancelling,
    onDelete: deletePrompt.ask,
    onSend: setSending,
    origin,
  });

  // Registrul unui client crește cu fiecare anexă — 83 de abonamente înseamnă 84 de
  // documente din prima zi. Paginarea nu e un moft de listă mare, e ce împiedică ecranul să
  // ceară tot.
  const lastPage = query.data ? Math.max(1, Math.ceil(query.data.total / query.data.perPage)) : 1;

  return (
    <>
      <SectionContainer>
        <SectionHeader
          title="Documente"
          actions={
            // Butonul dispare pe un client arhivat, și în locul lui se spune de ce.
            // ⚠️ **Ascunderea nu e apărarea** — serverul refuză oricum (`CustomerArchivedError`):
            // un document generat pe un client arhivat consumă un număr din registru, iar
            // numărul nu se mai întoarce. Aici e doar ca operatorul să nu ceară degeaba.
            !customerId ? null : (
              <div className="flex items-center gap-x-2">
                {/* Filtrul rămâne **și pe un client arhivat**: acolo nu se mai emit acte, dar
                    tocmai acolo se caută unul vechi. */}
                <ArchivedDocumentsFilter
                  id="filtru-documente-arhivate-client"
                  value={documentArchived}
                  onChange={(value) => {
                    setDocumentArchived(value);
                    // Altfel ai rămâne pe „pagina 3" a unei liste care acum are una.
                    setPage(1);
                  }}
                />
                {archived ? (
                  <Text size="small" className="text-ui-fg-muted">
                    Client arhivat — nu se mai emit documente
                  </Text>
                ) : (
                  <>
                    {/* Încărcarea stă **lângă** generare, nu într-un meniu: un act adus de
                    client pe hârtie e la fel de obișnuit ca unul emis de noi, iar amândouă
                    consumă un număr din același registru (#108). */}
                    <Button size="small" variant="secondary" onClick={() => setUploading(true)}>
                      <ArrowUpTray />
                      Încarcă
                    </Button>
                    <Button size="small" variant="secondary" onClick={() => setGenerating(true)}>
                      <Plus />
                      Generează
                    </Button>
                  </>
                )}
              </div>
            )
          }
        />

        {!customerId ? (
          <SectionEmpty>Salvează întâi fișa clientului, apoi generează documente.</SectionEmpty>
        ) : query.isPending ? (
          <div className="flex flex-col gap-3 px-6 py-4">
            <Skeleton className="h-5 w-full" />
            <Skeleton className="h-5 w-2/3" />
          </div>
        ) : query.error ? (
          <SectionEmpty tone="error">
            {query.error instanceof Error
              ? query.error.message
              : 'Documentele nu au putut fi citite.'}
          </SectionEmpty>
        ) : query.data.items.length === 0 ? (
          <SectionEmpty>
            {documentArchived === 'only'
              ? 'Niciun document arhivat.'
              : documentArchived === 'all'
                ? 'Niciun document.'
                : // Fără filtru, lista ascunde arhivatele — „Niciun document" ar fi fost o
                  // minciună pe un client care are doar acte arhivate. Cu filtrul pe „și
                  // arhivate", nu mai e: chiar n-are niciunul.
                  'Niciun document. Cele arhivate se văd cu filtrul de sus.'}
          </SectionEmpty>
        ) : (
          <>
            <Table>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell className="w-[90px]">Nr.</Table.HeaderCell>
                  <Table.HeaderCell>Tip</Table.HeaderCell>
                  <Table.HeaderCell>Abonament</Table.HeaderCell>
                  <Table.HeaderCell className="w-[120px]">Data</Table.HeaderCell>
                  <Table.HeaderCell className="w-[140px]">Stare</Table.HeaderCell>
                  <Table.HeaderCell className="w-[60px]" />
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {query.data.items.map((document) => (
                  <DocumentRow
                    key={document.id}
                    document={document}
                    groups={actionsOf(document)}
                    origin={origin}
                  />
                ))}
              </Table.Body>
            </Table>

            {lastPage > 1 ? (
              <div className="flex items-center justify-between gap-4 px-6 py-4">
                <Text size="small" className="text-ui-fg-subtle">
                  Pagina {query.data.page} din {lastPage} · {query.data.total} documente
                </Text>
                <div className="flex items-center gap-x-2">
                  <Button
                    size="small"
                    variant="secondary"
                    disabled={page <= 1}
                    onClick={() => setPage((current) => current - 1)}
                  >
                    Înapoi
                  </Button>
                  <Button
                    size="small"
                    variant="secondary"
                    disabled={page >= lastPage}
                    onClick={() => setPage((current) => current + 1)}
                  >
                    Înainte
                  </Button>
                </div>
              </div>
            ) : null}
          </>
        )}
      </SectionContainer>

      {customerId ? (
        <>
          <GenerateDrawer
            customerId={customerId}
            open={generating}
            onClose={() => setGenerating(false)}
          />
          <UploadDocumentDrawer
            owner={{ kind: 'customer', customerId }}
            open={uploading}
            onClose={() => setUploading(false)}
          />
        </>
      ) : null}

      <CancelDocumentDrawer document={cancelling} onClose={() => setCancelling(null)} />
      {deletePrompt.element}

      <SendToSigningDrawer document={sending} onClose={() => setSending(null)} />
    </>
  );
}

/**
 * Un rând din registrul clientului. **Clicul îl deschide**, ca în „Documente emise"
 * (`documents-screen.tsx`).
 *
 * Era singura listă de documente din aplicație în care actul se deschidea dintr-o acțiune de
 * meniu, iar acțiunea îl scotea într-o filă nouă, ca fișier — fondatorul, 2026-09-21:
 * *„la registru documente când dau click se deschide documentul înăuntru. aș vrea să fie la
 * fel și la clienți. acum se deschide un link, nu îmi place"*. Două gesturi diferite pentru
 * același act, pe două ecrane care arată aceleași coloane.
 *
 * ⚠️ **Meniul rămâne neatins**, inclusiv „Vezi PDF": el cere fișierul, care e altceva decât
 * pagina actului. Ce s-a schimbat e gestul implicit, nu ce se putea face înainte.
 */
function DocumentRow({
  document,
  groups,
  origin,
}: {
  document: DocumentListItem;
  groups: readonly ActionMenuGroup[];
  origin: DocumentOrigin | undefined;
}) {
  const navigate = useNavigate();

  return (
    <Table.Row
      {...archivedRowProps(document, 'cursor-pointer')}
      onClick={() =>
        void navigate({
          to: '/documente/$documentId',
          params: { documentId: document.id },
          search: origin ?? {},
        })
      }
    >
      <Table.Cell className="tabular-nums">
        <DocumentNumberCell document={document} />
      </Table.Cell>
      <Table.Cell>
        <div className="flex items-center gap-x-2">
          {document.documentTypeName}
          <DocumentSourceMark document={document} />
        </div>
      </Table.Cell>
      {/* `-`, nu gol: documentul de client chiar n-are abonament, iar spațiul liber s-ar
          citi ca o coloană pe care ecranul a uitat s-o completeze. Un contract acoperă de
          obicei câteva servicii, deci se scriu toate — un „3 abonamente" ar fi ascuns exact
          ce vrea operatorul să vadă când caută actul unui serviciu. */}
      <Table.Cell className="text-ui-fg-subtle">
        {document.subscriptions.length === 0
          ? '-'
          : document.subscriptions.map((subscription) => subscription.name).join(', ')}
      </Table.Cell>
      <Table.Cell className="text-ui-fg-subtle">{formatDate(document.documentDate)}</Table.Cell>
      <Table.Cell>
        <DocumentStateCell document={document} />
      </Table.Cell>
      {/*
        ⚠️ **Clicul pe meniu nu urcă la rând** (#222). Fără `stopPropagation`, butonul meniului
        deschidea documentul în loc de meniu — iar evenimentele React urcă și prin portalul
        meniului, deci și o opțiune aleasă ar fi navigat. La fel ca în registru.
      */}
      <Table.Cell onClick={(event) => event.stopPropagation()}>
        <ActionMenu groups={groups} />
      </Table.Cell>
    </Table.Row>
  );
}

/**
 * Generarea: tipul de document, template-ul publicat, câmpurile lui, apoi abonamentele.
 *
 * Ordinea nu e cosmetică — fiecare pas îl deschide pe următorul. Tipul decide care
 * template-uri intră în listă, template-ul ales decide ce câmpuri se completează, iar lista
 * de abonamente decide câte documente ies: goală înseamnă un document al clientului, trei
 * bifate înseamnă trei documente cu trei numere. **Generarea în masă e cerința** — un client
 * cu 83 de abonamente înseamnă 83 de anexe, iar ecranul n-are voie să le ceară una câte una.
 *
 * ⚠️ **Ce se poate opri, se oprește aici.** Un câmp obligatoriu necompletat nu ajunge la
 * server: refuzat acolo, tot n-ar consuma un număr — serverul verifică înainte de alocare —
 * dar operatorul ar afla-o după o cerere, cu formularul deja închis peste el. Verificarea
 * din ecran e a doua plasă, nu singura: ce contează pentru registru rămâne pe server.
 */
function GenerateDrawer({
  customerId,
  open,
  onClose,
}: {
  customerId: string;
  open: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const documentTypes = useQuery(nomenclatorQueryOptions('tipuri-document'));
  const subscriptions = useQuery(subscriptionsQueryOptions(customerId));

  const [documentTypeId, setDocumentTypeId] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [values, setValues] = useState<Record<string, DocumentFieldValue>>({});
  /** Erorile de câmp apar **după** prima apăsare, nu în timp ce se scrie în formular. */
  const [submitted, setSubmitted] = useState(false);
  const [selected, setSelected] = useState<readonly string[]>([]);

  const templates = useQuery(generationTemplatesQueryOptions({ customerId, documentTypeId }));
  const form = useQuery(templateFormQueryOptions(templateId));

  useEffect(() => {
    if (open) {
      setDocumentTypeId('');
      setTemplateId('');
      setValues({});
      setSubmitted(false);
      setSelected([]);
    }
  }, [open]);

  const availableTemplates = templates.data ?? [];

  // Un singur template publicat pentru tipul ăsta e cazul obișnuit: un select cu o singură
  // opțiune, pe care operatorul o alege de fiecare dată, e o apăsare cerută degeaba.
  useEffect(() => {
    const only = templates.data;

    if (only !== undefined && only.length === 1 && templateId === '') {
      setTemplateId(only[0].id);
    }
  }, [templates.data, templateId]);

  // Valorile implicite ale autorului, puse în formular ca text de pornire — nu ca valoare
  // ascunsă trimisă în locul unui răspuns.
  useEffect(() => {
    if (form.data === undefined) {
      return;
    }

    setValues(Object.fromEntries(form.data.map((field) => [field.id, initialValue(field)])));
    // `templateId` în dependențe, nu doar `form.data`: la redeschiderea sertarului pe
    // același template, răspunsul vine din cache cu aceeași identitate, iar fără el
    // formularul ar rămâne golit de resetarea de la deschidere.
  }, [form.data, templateId]);

  // Doar tipurile în uz: unul dezactivat rămâne pe documentele emise cu el, dar serverul
  // refuză să mai producă altele.
  const availableTypes = (documentTypes.data ?? []).filter((type) => type.status);
  const availableSubscriptions = subscriptions.data ?? [];
  const documentTypeName =
    availableTypes.find((type) => type.id === documentTypeId)?.name ?? 'Tipul ales';

  const fields = form.data ?? [];
  const missing = fields.filter((field) => field.required && (values[field.id]?.text ?? '') === '');

  /** Fără template publicat nu se generează: mesajul spune asta, nu „a eșuat". */
  const noTemplates =
    documentTypeId !== '' &&
    !templates.isPending &&
    templates.error === null &&
    availableTemplates.length === 0;

  const mutation = useMutation({
    mutationFn: () =>
      generateDocument({
        customerId,
        documentTypeId,
        templateId,
        fieldValues: values,
        subscriptionIds: selected,
      }),
    onSuccess: async () => {
      // ⚠️ **Fără număr, și cu vorba potrivită** (#142): generarea nu emite nimic, face o
      // ciornă. Un „Document generat cu numărul 71" era chiar propoziția care a produs
      // tichetul — numărul vine la trimiterea la semnat (#221).
      toast.success('Ciornă generată', {
        description:
          'Primește număr de registru când o trimiți la semnat. Până atunci se poate edita sau șterge.',
      });
      await queryClient.invalidateQueries({ queryKey: DOCUMENTS_QUERY_KEY });
      onClose();
    },
    onError: (error: unknown) => {
      // Mesajul serverului: „Clientul nu are contract-cadru în registru…" spune și ce e de
      // făcut. Un text inventat aici ar fi al doilea adevăr despre aceeași regulă.
      toast.error(error instanceof ApiError ? error.message : 'Documentul nu a putut fi generat.');
    },
  });

  const allSelected =
    availableSubscriptions.length > 0 && selected.length === availableSubscriptions.length;

  function genereaza() {
    setSubmitted(true);

    if (missing.length > 0) {
      return;
    }

    mutation.mutate();
  }

  return (
    <Drawer open={open} onOpenChange={(next) => !next && onClose()}>
      <Drawer.Content>
        <Drawer.Header>
          <Drawer.Title>Generează document</Drawer.Title>
        </Drawer.Header>

        <Drawer.Body className="flex flex-col gap-y-4 overflow-y-auto">
          <Field label="Tip de document" id="document-tip" required>
            <Select
              value={documentTypeId}
              onValueChange={(value) => {
                setDocumentTypeId(value);
                // Template-ul de la tipul dinainte n-are ce căuta pe ăsta, iar câmpurile lui
                // cu atât mai puțin: rămase, s-ar trimite răspunsuri la întrebări care nu se
                // mai pun.
                setTemplateId('');
                setValues({});
                setSubmitted(false);
              }}
            >
              <Select.Trigger id="document-tip">
                <Select.Value placeholder="Alege" />
              </Select.Trigger>
              {/* `z-50` ca peste tot în aplicație: fără el, lista se randează **sub**
                  overlay-ul sertarului, iar dropdown-ul pare gol. */}
              <Select.Content className="z-50">
                {availableTypes.map((type) => (
                  <Select.Item key={type.id} value={type.id}>
                    {type.name}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select>
          </Field>

          {documentTypeId === '' ? null : templates.isPending ? (
            <Skeleton className="h-9 w-full" />
          ) : templates.error ? (
            <Text size="small" className="text-ui-fg-error">
              {templates.error instanceof Error
                ? templates.error.message
                : 'Template-urile nu au putut fi citite.'}
            </Text>
          ) : noTemplates ? (
            <Text size="small" className="text-ui-fg-error">
              „{documentTypeName}" n-are niciun template publicat, deci nu se poate genera. Publică
              unul din Template-uri de document și încearcă din nou.
            </Text>
          ) : (
            <Field label="Template" id="document-template" required>
              <Select
                value={templateId}
                onValueChange={(value) => {
                  setTemplateId(value);
                  setSubmitted(false);
                }}
              >
                <Select.Trigger id="document-template">
                  <Select.Value placeholder="Alege" />
                </Select.Trigger>
                <Select.Content className="z-50">
                  {availableTemplates.map((template) => (
                    <Select.Item key={template.id} value={template.id}>
                      {template.name} · v{template.version}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select>
            </Field>
          )}

          {templateId === '' ? null : form.isPending ? (
            <Skeleton className="h-9 w-full" />
          ) : form.error ? (
            <Text size="small" className="text-ui-fg-error">
              {form.error instanceof Error
                ? form.error.message
                : 'Câmpurile template-ului nu au putut fi citite.'}
            </Text>
          ) : fields.length === 0 ? null : (
            <div className="flex flex-col gap-y-4 rounded-lg border px-3 py-4">
              <Label>De completat</Label>
              {fields.map((field) => (
                <TemplateFieldInput
                  key={field.id}
                  field={field}
                  value={values[field.id] ?? { id: '', text: '' }}
                  invalid={submitted && field.required && (values[field.id]?.text ?? '') === ''}
                  disabled={mutation.isPending}
                  onChange={(next) => setValues((current) => ({ ...current, [field.id]: next }))}
                />
              ))}
            </div>
          )}

          {availableSubscriptions.length > 0 ? (
            <div className="flex flex-col gap-y-2">
              <div className="flex items-center justify-between">
                <Label>Abonamente</Label>
                <button
                  type="button"
                  className="text-ui-fg-interactive text-xs hover:underline"
                  onClick={() =>
                    setSelected(
                      allSelected
                        ? []
                        : availableSubscriptions.map((subscription) => subscription.id),
                    )
                  }
                >
                  {allSelected ? 'Deselectează tot' : 'Selectează tot'}
                </button>
              </div>

              <ul className="divide-y rounded-lg border">
                {availableSubscriptions.map((subscription) => {
                  const checked = selected.includes(subscription.id);
                  return (
                    <li key={subscription.id} className="flex items-center gap-x-3 px-3 py-2">
                      <Checkbox
                        id={`abonament-${subscription.id}`}
                        checked={checked}
                        onCheckedChange={() =>
                          setSelected((current) =>
                            checked
                              ? current.filter((id) => id !== subscription.id)
                              : [...current, subscription.id],
                          )
                        }
                        disabled={mutation.isPending}
                      />
                      <Label htmlFor={`abonament-${subscription.id}`} className="flex-1">
                        {subscription.name}
                      </Label>
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : null}
        </Drawer.Body>

        <Drawer.Footer>
          {/* Motivul stă lângă butonul stins, nu într-un toast de după apăsare: altfel
              operatorul vede „Generează" inactiv și n-are de unde ști ce-i lipsește. */}
          {submitted && missing.length > 0 ? (
            <Text size="small" className="text-ui-fg-error mr-auto">
              Completează: {missing.map((field) => field.label).join(', ')}.
            </Text>
          ) : null}
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
            Renunță
          </Button>
          <Button
            onClick={genereaza}
            isLoading={mutation.isPending}
            disabled={mutation.isPending || documentTypeId === '' || templateId === ''}
          >
            Generează
          </Button>
        </Drawer.Footer>
      </Drawer.Content>
    </Drawer>
  );
}

/**
 * Valoarea de pornire a unui câmp.
 *
 * Un „Da / Nu" **pornește răspuns**, nu gol: întrebarea are întotdeauna un răspuns, iar un
 * comutator stins înseamnă „Nu", nu „n-am completat". Restul pornesc de la ce a scris
 * autorul template-ului ca implicit, sau goale.
 */
function initialValue(field: TemplateFormField): DocumentFieldValue {
  const preset = field.defaultValue ?? '';

  if (field.dataType === 'boolean') {
    return { id: preset === 'Da' ? 'Da' : 'Nu', text: preset === 'Da' ? 'Da' : 'Nu' };
  }

  // Pentru un select, implicitul autorului e o cheie doar dacă se regăsește în listă; altfel
  // ar ajunge în document ca text al unui rând care nu există.
  if (field.options !== null) {
    const found = field.options.find((option) => option.value === preset);

    return found === undefined ? { id: '', text: '' } : { id: found.value, text: found.label };
  }

  return { id: preset, text: preset };
}

/**
 * Un câmp al formularului de generare.
 *
 * ⚠️ **Pentru un select se salvează perechea, nu cheia.** `{ id, text }`: id-ul ca să se
 * poată urmări de unde a venit, textul pentru că el se tipărește. Un pachet redenumit peste
 * un an ar face ca documentul retipărit să arate altceva decât cel semnat.
 */
function TemplateFieldInput({
  field,
  value,
  invalid,
  disabled,
  onChange,
}: {
  field: TemplateFormField;
  value: DocumentFieldValue;
  invalid: boolean;
  disabled: boolean;
  onChange: (next: DocumentFieldValue) => void;
}) {
  const id = `camp-${field.id}`;
  const error = invalid ? 'Câmp obligatoriu.' : undefined;

  if (field.options !== null) {
    // O sursă care nu mai citește — tabel redenumit, coloană ștearsă — lasă lista goală.
    // Spus, nu ascuns: un select gol fără explicație pare o eroare a ecranului.
    const options = field.options.filter((option) => option.value !== '');

    return (
      <Field
        label={field.label}
        id={id}
        required={field.required}
        error={error}
        hint={options.length === 0 ? 'Sursa de date nu are rânduri.' : undefined}
      >
        <Select
          value={value.id}
          onValueChange={(next) =>
            onChange({
              id: next,
              text: options.find((option) => option.value === next)?.label ?? next,
            })
          }
          disabled={disabled || options.length === 0}
        >
          <Select.Trigger id={id}>
            <Select.Value placeholder="Alege" />
          </Select.Trigger>
          <Select.Content className="z-50">
            {options.map((option) => (
              <Select.Item key={option.value} value={option.value}>
                {option.label}
              </Select.Item>
            ))}
          </Select.Content>
        </Select>
      </Field>
    );
  }

  if (field.dataType === 'boolean') {
    return (
      <div className="flex items-center justify-between gap-x-3">
        <Label htmlFor={id}>{field.label}</Label>
        <Switch
          id={id}
          checked={value.text === 'Da'}
          onCheckedChange={(checked) =>
            onChange({ id: checked ? 'Da' : 'Nu', text: checked ? 'Da' : 'Nu' })
          }
          disabled={disabled}
        />
      </div>
    );
  }

  return (
    <Field label={field.label} id={id} required={field.required} error={error}>
      <Input
        id={id}
        type={field.dataType === 'number' ? 'number' : field.dataType === 'date' ? 'date' : 'text'}
        value={value.text}
        onChange={(event) => onChange({ id: event.target.value, text: event.target.value })}
        disabled={disabled}
        autoComplete="off"
      />
    </Field>
  );
}
